import DeliveryZonesService from '@/api/services/delivery-zones.service';
import OrdersService from '@/api/services/orders.service';
import PaymentsService from '@/api/services/payments.service';
import { OrderType, OrderMode } from '@/enums/OrderEnums';
import { PaymentMethod } from '@/enums/PaymentMethod';
import { RoundMode } from '@/enums/RoundMode';
import { TaxDisplayMode } from '@/enums/TaxDisplayMode';
import { IOrder } from '@/interfaces/IOrder';
import { useOrdersStore } from '@/stores/orders.store';
import { applyRound, roundPrice, round2 } from '@/utils';
import { useLocalStorage, whenever, useTimeoutFn } from '@vueuse/core';
import {
  reactive,
  watch,
  computed,
  toRefs,
  onMounted,
  ref,
  nextTick,
} from 'vue';
import { useMenuStore } from './use-menu';
import { useRestaurant } from './use-restaurant';
import { useUi } from './use-ui';
import { IDeliveryInfo } from '@/interfaces/IDeliveryInfo';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { ICart, ICartItem } from '@/interfaces/ICart';
import { IDeliveryZone } from '@/interfaces/IDeliveryZone';
import { getContainingDeliveryZone } from '@/utils/polygon.utils';
import { ITable } from '@/interfaces/ITable';
import { orderItemToCartItem } from '@/utils/orders.utils';
import { compareCartItems } from '@/utils/cart.utils';
import ICustomField from '@/interfaces/ICustomField';
import { AlertType } from '@/interfaces/IAlert';
import { CustomError } from '@/exceptions/custom.exception';
import { useSocketStore } from './use-socket';
import { createClientOrder } from '@/utils/ordering.utils';
import { useOrders } from './use-orders';
import { v4 as uuidv4 } from 'uuid';
import { Globals } from '@/modules/Globals';
import { ICustomer } from '@/interfaces/ICustomer';

const defaultCart = {
  restaurantId: null,
  cartItems: [],
  nextIndex: 0,
  userComment: '',
} as ICart;

export function useOrdering() {
  const restaurantHelper = useRestaurant();
  const restaurantStore = useRestaurantStore();
  const menuStore = useMenuStore();
  const ordersStore = useOrdersStore();
  const ui = useUi();
  const socket = useSocketStore();
  const ordersHelper = useOrders();

  console.log('Begin order session');

  const state = reactive({
    editOrder: null as IOrder | null,
    cart: null as ICart | null,
    table: null as ITable | null,
    /** Party size (Seat orders; informative only). */
    covers: null as number | null,
    deliveryInfo: null as IDeliveryInfo | null,
    paymentMethod: null as PaymentMethod | null,
    customerData: {
      name: '',
      phone: '',
    },
    selectedCustomer: null as ICustomer | null,
    customFieldValues: null as null | Record<string, string>,
    status: 'none' as 'none' | 'creating' | 'success' | 'error',
  });

  //#region Cart
  function resetCart() {
    const cart = JSON.parse(JSON.stringify(defaultCart));
    cart.restaurantId = restaurantStore.restaurant!.id;
    state.cart = cart;
  }

  resetCart();

  function addItem(item: ICartItem) {
    for (const cartItem of state.cart!.cartItems) {
      const comparisonResult = compareCartItems(cartItem, item);
      if (comparisonResult === 'equal' || comparisonResult === 'amount') {
        cartItem.quantity += item.quantity;
        cartItem.totalPrice = roundPrice(
          cartItem.unitPrice * cartItem.quantity
        );
        return;
      }
    }

    item.index = state.cart!.nextIndex++;
    state.cart!.cartItems.push(item);
  }

  const currentCartTotal = computed(() => {
    if (!state.cart) return 0;

    return state.cart.cartItems.reduce((acc, item) => {
      return acc + item.totalPrice;
    }, 0);
  });

  const currentCartTotalItems = computed(() => {
    if (!state.cart) return 0;

    return state.cart.cartItems.reduce((acc, item) => {
      return acc + item.quantity;
    }, 0);
  });

  const orderTypeToCFOrderType = {
    [OrderType.Seat]: 's',
    [OrderType.Away]: 't',
    [OrderType.Delivery]: 'd',
  };
  const activeCustomFields = computed(() => {
    const cF = restaurantStore.customFields;
    if (!cF) return cF;

    return cF.filter((cF) =>
      cF.orderTypes!.includes(
        orderTypeToCFOrderType[menuStore.selectedOrderType!]
      )
    );
  });
  //#endregion

  watch(
    activeCustomFields,
    (fields: ICustomField[] | null) => {
      if (!fields) {
        state.customFieldValues = {};
        return;
      }

      state.customFieldValues = fields.reduce((acc, field) => {
        acc[field.id] = '';
        return acc;
      }, {} as Record<string, string>);

      if (state.editOrder) {
        if (state.editOrder.orderFields) {
          for (const field of state.editOrder.orderFields) {
            state.customFieldValues[field.fieldId] = field.value; //TODO fix
          }
        }
      }
    },
    { immediate: true }
  );

  const currentDeliveryZone = computed(() => {
    const branch = restaurantStore.selectedBranch,
      orderType = menuStore.selectedOrderType,
      deliveryZones = restaurantStore.deliveryZones;

    if (!branch || !orderType) return null;

    if (orderType !== OrderType.Delivery) return null;

    if (!branch.zonedDelivery || !state.deliveryInfo) return null;

    if (!deliveryZones) return null;

    return getContainingDeliveryZone(
      [state.deliveryInfo.lat!, state.deliveryInfo.lng!],
      deliveryZones
    );
  });

  const orderCostSummary = computed(() => {
    const restaurant = restaurantStore.restaurant,
      branch = restaurantStore.selectedBranch,
      settings = restaurantStore.restaurantSettings,
      orderType = menuStore.selectedOrderType;

    const costs = {
      delivery: 0,
      deliveryTax: 0,
      products: 0,
      productsTax: 0,
      total: 0,
      roundDifference: 0,
    };

    if (!restaurant || !branch || !settings || !state.cart || !orderType)
      return costs;

    if (orderType === OrderType.Delivery) {
      costs.delivery = branch.deliveryCost ?? 0;

      if (currentDeliveryZone.value && branch.zonedFee) {
        costs.delivery = currentDeliveryZone.value.cost ?? costs.delivery;
      }

      if (settings.taxDelivery) {
        const taxedDelivery = restaurantHelper.applyTax({
          price: costs.delivery,
          partial: true,
          forced: true,
        });

        if (settings.taxMode === TaxDisplayMode.AddAtInvoice) {
          costs.deliveryTax = taxedDelivery - costs.delivery;
        } else {
          costs.delivery = taxedDelivery;
        }
      }
    }

    costs.products = currentCartTotal.value;

    if (settings.taxMode === TaxDisplayMode.AddAtInvoice) {
      costs.productsTax =
        restaurantHelper.applyTax({
          price: costs.products,
          partial: false,
          forced: true,
        }) - costs.products;
    }

    costs.total =
      costs.delivery + costs.products + costs.deliveryTax + costs.productsTax;

    if (restaurant.settings!.roundMode !== RoundMode.None) {
      const roundedTotal = applyRound(
        costs.total,
        restaurant.settings!.roundMode!,
        restaurant.settings!.roundInterval!
      );

      costs.roundDifference = roundPrice(roundedTotal - costs.total);
      costs.total = roundedTotal;
    }

    return costs;
  });

  const orderCostTotal = computed(() => {
    if (
      restaurantStore.restaurantSettings?.taxMode ===
      TaxDisplayMode.AddAtInvoice
    ) {
      return Object.values(orderCostSummary.value).reduce(
        (acc, val) => acc + val,
        0
      );
    } else {
      return orderCostSummary.value.delivery + orderCostSummary.value.products;
    }
  });

  const amountLeftForDelivery = computed(() => {
    const menuHasDelivery = menuStore.availableOrderTypesInCurrentMenu?.some?.(
      (t) => t.type === OrderType.Delivery
    );
    if (!menuHasDelivery) return 0;

    const minimumAmount = restaurantStore.selectedBranch?.deliveryMin || 0;

    return Math.max(0, round2(minimumAmount - currentCartTotal.value));
  });

  const isOutsideDeliveryZone = computed(() => {
    return (
      state.deliveryInfo &&
      restaurantStore.selectedBranch?.zonedDelivery &&
      restaurantStore.deliveryZones?.length &&
      !currentDeliveryZone.value
    );
  });

  const errors = computed(() => {
    const errors: string[] = [];

    if (!currentCartTotalItems.value) {
      errors.push('Agregue productos al carrito');
    }

    if (menuStore.selectedOrderType === OrderType.Delivery) {
      if (!state.deliveryInfo) errors.push('Defina la dirección de entrega');
    } else if (menuStore.selectedOrderType === OrderType.Seat) {
      if (!state.table) errors.push('Seleccione una mesa');
    }

    if (activeCustomFields.value?.length) {
      for (const field of activeCustomFields.value) {
        if (field.mandatory && !state.customFieldValues?.[field.id]) {
          errors.push(`Ingrese ${field.title}`);
        }
      }
    }

    return errors;
  });

  /**
   * Sets a customer as the current customer for the order.
   * This will automatically map their basic data to the order helper state.
   */

  watch(
    [() => state.selectedCustomer, () => menuStore.selectedOrderType],
    ([customer, orderType], [oldCustomer, oldOrderType]) => {
      if (customer) {
        state.customerData.name = customer.name;
        state.customerData.phone = customer.tel ?? '';

        if (orderType === OrderType.Delivery) {
          if (customer.address) {
            state.deliveryInfo = {
              address: customer.address,
              address2: customer.address2 ?? '',
              addressComment: customer.addressComment ?? '',
              lat: customer.lat ?? undefined,
              lng: customer.lng ?? undefined,
            } as IDeliveryInfo;
          } else {
            state.deliveryInfo = null;
          }
        }
      } else if (oldCustomer) {
        state.customerData.name = '';
        state.customerData.phone = '';
        state.deliveryInfo = null;
        state.table = null;
      }
    },
    { immediate: true }
  );

  async function createOrder() {
    try {
      // Prevent manual order creation in WhatsApp mode
      if (restaurantStore.selectedBranch!.ordersMode === OrderMode.WhatsApp) {
        throw new CustomError(
          'La creación manual de pedidos está deshabilitada en modo WhatsApp'
        );
      }

      // When offline ordering disabled, block manual creation without connection
      if (!socket.isConnected && !Globals.offlineOrderingEnabled) {
        throw new CustomError(
          'No se pudo conectar al servidor, verifique su conexión a internet'
        );
      }
      // Otherwise, if enabled, we will fall back to local order creation

      if (errors.value.length) {
        const firstError = errors.value[0];
        throw new CustomError(firstError);
      }

      state.status = 'creating';

      const localOrderId = uuidv4();

      const orderData = {
        restaurantId: restaurantStore.restaurant!.id,
        branchId: restaurantStore.selectedBranch!.id,
        orderType: menuStore.selectedOrderType,
        cartItems: state.cart!.cartItems,
        userComment: state.cart!.userComment,
        deliveryInfo: state.deliveryInfo,
        paymentMethod: state.paymentMethod,
        customerData: state.customerData,
        customFieldValues: state.customFieldValues,
        customerId: state.selectedCustomer?.id ?? undefined,
        tableId:
          menuStore.selectedOrderType === OrderType.Seat
            ? state.table?.id
            : undefined,
        // Create: only sent when set (absent = none). Edit: always explicit —
        // a number updates, null clears the stored value server-side.
        covers:
          menuStore.selectedOrderType === OrderType.Seat
            ? state.covers ?? (state.editOrder ? null : undefined)
            : undefined,
        manual: true,
        deviceId: restaurantStore.deviceId,
        localId: localOrderId,
      };

      //console.log("Creating order", orderData);

      let order: IOrder;

      if (!state.editOrder) {
        /*if (socket.isConnected) {
          order = await OrdersService.v2Create(orderData);
        } else {
          const clientOrder = createClientOrder({
            restaurant: restaurantStore.restaurant!,
            branch: restaurantStore.selectedBranch!,
            settings: restaurantStore.restaurantSettings!,
            customFields: activeCustomFields.value!,
            cart: state.cart!,
            table: state.table,
            deliveryZones: restaurantStore.deliveryZones,
            ...orderData,
          });
          ordersHelper.openOrder(clientOrder);
        }*/
        order = await OrdersService.v2Create(orderData);

        useTimeoutFn(() => {
          if (!ordersStore.orders.find((o) => o.id === order.id)) {
            ordersHelper.refreshOrder({ orderId: order.id }).catch((err) => {
              console.error('Error refreshing order after create error', err);
            });
          }
        }, 1000);
      } else {
        order = await OrdersService.v2Update(state.editOrder.id, orderData);
      }

      state.status = 'success';

      //resetCart();

      return order;
    } catch (e) {
      state.status = 'error';
      throw e;
    }
  }

  async function setEditOrder(order: IOrder) {
    if (order.type !== menuStore.selectedOrderType) {
      menuStore.setSelectedOrderType(order.type);
      await nextTick();
    }
    state.customerData.name = order.userName || '';
    state.customerData.phone = order.userTel || order.userWhatsapp || '';
    state.cart.userComment = order.comment || '';

    switch (order.type) {
      case OrderType.Delivery:
        state.deliveryInfo = {
          address: order.userAddress || '',
          address2: order.userAddress2 || '',
          addressComment: order.userAddressComments || '',
          lat: order.deliveryLat,
          lng: order.deliveryLng,
        };
        break;
      case OrderType.Seat:
        state.table = order.table;
        state.covers = order.covers ?? null;
        break;
    }
    state.paymentMethod = order.paymentMethod;

    if (order.orderFields) {
      let customFieldValues: Record<string, string>;
      if (activeCustomFields.value) {
        customFieldValues = activeCustomFields.value.reduce((acc, field) => {
          acc[field.id] = '';
          return acc;
        }, {} as Record<string, string>);
      } else {
        console.warn('No custom fields found when setting edit order');
        customFieldValues = {};
      }

      for (const field of order.orderFields) {
        customFieldValues[field.fieldId] = field.value;
      }

      state.customFieldValues = customFieldValues;
    }

    for (const orderItem of order.orderItems) {
      if (orderItem.canceledAt) continue;

      const cartItem = orderItemToCartItem(orderItem);
      const menuItem = menuStore.itemMap?.[cartItem.itemId];
      if (menuItem) {
        cartItem.item = menuItem;
      }
      state.cart.cartItems.push(cartItem);
      state.cart.nextIndex = Math.max(state.cart.nextIndex, cartItem.index + 1);
    }

    state.editOrder = order;
    state.selectedCustomer = order.customer ?? null;
  }

  return {
    ...toRefs(state),
    currentDeliveryZone,
    isOutsideDeliveryZone,
    orderCostSummary,
    orderCostTotal,
    amountLeftForDelivery,
    errors,
    currentCartTotal,
    currentCartTotalItems,
    setEditOrder,
    createOrder,
    addItem,
  };
}
